import { isObject } from '#object/is-object';

import { isDefined } from './is-defined.guard';
import { isFunction } from './is-function.guard';
import { isInstanceOf } from './is-instance-of';

/**
 * Determines whether the provided value is a Promise.
 *
 * This type guard checks if the value is an instance of `Promise` or
 * if it is an object with `then`, `catch`, and `finally` methods,
 * which are typical characteristics of a Promise-like object (thenable).
 *
 * @typeParam TData - The type of the resolved value of the Promise.
 * @param value - The value to check.
 * @returns `true` if the value is a Promise or thenable, otherwise `false`.
 *
 * @example
 * ```typescript
 * const promise = new Promise((resolve) => resolve('Hello, World!'));
 * console.log(isPromise(promise)); // true
 * ```
 * @example
 * ```typescript
 * const thenable = {
 *   then: (resolve: (value: string) => void) => resolve('Hello, World!'),
 *  catch: () => {},
 *  finally: () => {}
 * };
 * console.log(isPromise(thenable)); // true
 * ```
 * @example
 * ```ts
 * // Using a non-Promise value
 * isPromise({ then: true }); // false
 * ```
 */
export function isPromise<TData = unknown>(value: unknown): value is Promise<TData> {
  return (
    isInstanceOf(value, Promise) ||
    (isDefined(value) &&
      isObject(value) &&
      'then' in value &&
      isFunction(value.then) &&
      'catch' in value &&
      isFunction(value.catch) &&
      'finally' in value &&
      isFunction(value.finally))
  );
}
